import { useEffect, useMemo, useRef, useState } from "react";
import { DOCUMENT_STATUS } from "@/profitability/document-constants";
import { useReportDocuments, useReportRun } from "@/profitability/query";
import { useReportDocumentIds } from "@/profitability/report-documents-picker";
import type {
  ProfitabilityDocument,
  ProfitabilityReport,
} from "@/profitability/types";

/**
 * The files a new run reads, slot by slot. Given `keep` (a run of the same
 * template: the period the files update), its files start in their slots,
 * except the one being replaced (`except`): adding the one file a period
 * lacks then needs nothing else. Choosing another period swaps the files
 * that came from the last one; a file the owner added stays. A reference
 * slot holds the file the current version was built from, never one carried
 * from an earlier run, until the owner adds another. Removing the kept file
 * empties the slot for a replacement; removing a replacement, or choosing to
 * use the kept file again, brings the kept one back.
 */
export const useRunDocuments = (
  businessId: string,
  report: ProfitabilityReport,
  keep?: { runId: string; except?: string | null } | null,
) => {
  const slots = report.inputs;
  const slotted = slots.length > 0;
  const [slotDocuments, setSlotDocuments] = useState<
    Record<string, ProfitabilityDocument | null>
  >({});
  const kept = useReportRun(businessId, slotted ? keep?.runId : undefined);
  // Which run's files are in the slots, and which slot holds which of them.
  const filled = useRef<{ runId: string | null; ids: Record<string, string> }>({
    runId: null,
    ids: {},
  });
  const keepId = keep?.runId ?? null;
  useEffect(() => {
    const run = keepId ? kept.data : null;
    if (filled.current.runId === keepId) return;
    if (keepId && (!run || run.id !== keepId || run.report?.id !== report.id))
      return;
    const previous = filled.current.ids;
    const byId = new Map(
      (run?.documents ?? []).map((document) => [document.id, document]),
    );
    const ids: Record<string, string> = {};
    const next = { ...slotDocuments };
    for (const slot of slots) {
      if (slot.reference) continue;
      const current = slotDocuments[slot.key];
      const fromLast =
        !!previous[slot.key] && current?.id === previous[slot.key];
      if (current && !fromLast) continue;
      const document = run ? byId.get(run.inputs[slot.key] ?? "") : undefined;
      if (document && slot.key !== keep?.except) {
        next[slot.key] = document;
        ids[slot.key] = document.id;
      } else if (fromLast) next[slot.key] = null;
    }
    filled.current = { runId: keepId, ids };
    setSlotDocuments(next);
  }, [kept.data, keepId, keep?.except, report.id, slots, slotDocuments]);
  const slotIds = slots
    .map((slot) => slotDocuments[slot.key]?.id)
    .filter((id): id is string => !!id);
  const slotQuery = useReportDocuments(businessId, slotIds);
  const keptIds = slots
    .filter((slot) => slot.reference && slot.document_id)
    .map((slot) => slot.document_id as string);
  const keptQuery = useReportDocuments(businessId, keptIds);
  const keptDocuments = useMemo(() => {
    const keptById = new Map((keptQuery.data ?? []).map((d) => [d.id, d]));
    return Object.fromEntries(
      slots.flatMap((slot): [string, ProfitabilityDocument][] => {
        const document =
          slot.reference && slot.document_id
            ? keptById.get(slot.document_id)
            : undefined;
        return document ? [[slot.key, document]] : [];
      }),
    );
  }, [slots, keptQuery.data]);
  const polledSlots = useMemo(() => {
    const byId = new Map((slotQuery.data ?? []).map((d) => [d.id, d]));
    return Object.fromEntries(
      slots.map((slot): [string, ProfitabilityDocument | null] => {
        const chosen = slotDocuments[slot.key];
        if (chosen === undefined && slot.reference)
          return [slot.key, keptDocuments[slot.key] ?? null];
        return [slot.key, chosen ? (byId.get(chosen.id) ?? chosen) : null];
      }),
    );
  }, [slots, slotDocuments, slotQuery.data, keptDocuments]);

  const [documentIds, setDocumentIds] = useState<string[]>([]);
  const documentsQuery = useReportDocuments(businessId, documentIds);
  const documents = slotted
    ? Object.values(polledSlots).filter((d): d is ProfitabilityDocument => !!d)
    : (documentsQuery.data ?? []);
  const picker = useReportDocumentIds(
    businessId,
    documentIds,
    documents,
    setDocumentIds,
  );

  const missing = slots.filter((slot) => !polledSlots[slot.key]);
  const reading = slotted
    ? documents.filter(
        (document) => document.status === DOCUMENT_STATUS.pending,
      )
    : [];
  const ready = slotted ? missing.length === 0 : documentIds.length > 0;
  const unreadable = documents.filter(
    (document) => document.status === DOCUMENT_STATUS.failed,
  );
  const keptUnchecked = slots.some(
    (slot) =>
      slot.reference &&
      slot.document_id &&
      slotDocuments[slot.key] === undefined &&
      keptQuery.isError,
  );
  const unchecked = slotted
    ? (slotIds.length > 0 && slotQuery.isError) || keptUnchecked
    : documentIds.length > 0 && documentsQuery.isError;
  const problem = unreadable.length
    ? `${unreadable.map((document) => document.file_name).join(", ")} could not be read. Remove it or upload a readable copy.`
    : unchecked
      ? "The documents could not be checked. Reload the page and add them again."
      : null;
  const inputs = Object.fromEntries(
    slots.flatMap((slot) => {
      const document = polledSlots[slot.key];
      return document ? [[slot.key, document.id]] : [];
    }),
  );

  return {
    slots,
    slotted,
    polledSlots,
    setSlotDocument: (key: string, document: ProfitabilityDocument | null) =>
      setSlotDocuments((current) => {
        const next = { ...current, [key]: document };
        // Removing the owner's replacement puts the kept file back; removing
        // the kept file (no entry yet) leaves the slot empty for another.
        if (
          !document &&
          current[key] &&
          slots.some((slot) => slot.key === key && slot.reference)
        )
          delete next[key];
        return next;
      }),
    /** The kept file of a reference slot the owner emptied, to put back. */
    restorable: (key: string) =>
      slotDocuments[key] === null ? (keptDocuments[key] ?? null) : null,
    restoreKept: (key: string) =>
      setSlotDocuments((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      }),
    documents,
    picker,
    missing,
    reading,
    ready,
    problem,
    request: {
      report_id: report.id,
      document_ids: slotted ? Object.values(inputs) : documentIds,
      ...(slotted ? { inputs } : {}),
    },
  };
};
